<template>
  <o-dialog
    ref="dialog"
    :title="title"
    :options="options"
    :valid="validate"
    :portal="portal"
  >
    <div v-if="serviceAssociation" class="pt-4 text-left">
      <o-form ref="form" class="h-full">
        <o-field-group
          v-if="serviceAssociation.isNewRecord"
          v-slot="{ status }"
          class="mb-4 block"
          rules="required"
          :label="$t('general.service')"
        >
          <service-select
            v-if="hasAddOnServices"
            v-model="target"
            :add-on-mode="[ServiceAssociationAddOnMode.allowed, ServiceAssociationAddOnMode.only]"
            :allow-internet="true"
            type="single"
            :status="status"
          />
          <OAlert
            v-else
            variant="warning"
            show
            :title="$t('components.service.actions.editAddOn.noAddOnServices')"
            class="text-sm"
          >
            <p>{{ $t('components.service.actions.editAddOn.noAddOnServicesInfo') }}</p>

            <o-panel-link path="/help-guide?path=services/creating-an-add-on-service" class="text-orange-600 text-sm flex items-center" @click="onClose">
              <span>{{ $t('components.service.actions.editAddOn.guide') }}</span> <o-icon icon="externalLink" size="10" class="pl-1" />
            </o-panel-link>
          </OAlert>
        </o-field-group>

        <o-field-group class="mb-4 block" :label="$t('components.service.actions.editAddOn.orderType')" :info="$t('components.service.actions.editAddOn.orderTypeInfo')">
          <radio-field v-model="serviceAssociation.orderType" :value="ServiceAssociationOrderType.After" :label="$t(ServiceAssociationOrderTypeLabels[ServiceAssociationOrderType.After], { service: service.title })" />
          <radio-field v-model="serviceAssociation.orderType" :value="ServiceAssociationOrderType.Before" :label="$t(ServiceAssociationOrderTypeLabels[ServiceAssociationOrderType.Before], { service: service.title })" />
        </o-field-group>

        <o-button v-if="target && !applyDiscount" :label="$t('components.service.actions.editAddOn.addDiscountToAddOn')" @click="onAddDiscount" />

        <o-field-group
          v-if="applyDiscount"
          v-slot="{ status }"
          class="mb-4 block"
          :rules="isDiscountTypePercentage ? 'between:0,100' : 'currency'"
          :label="$t('components.service.actions.editAddOn.discountAmount')"
          :message="!isDiscountTypePercentage && !!target ? $t('components.service.actions.editAddOn.maxDiscount', { price: target.price || 0 }) : ''"
        >
          <div class="flex items-center space-x-2">
            <currency-field
              v-if="!isDiscountTypePercentage"
              v-model="serviceAssociation.discountAmount"
              class="w-1/2"
              :status="status"
              :disabled="serviceAssociation.isNewRecord && !target"
              :max="target?.price"
            />
            <number-field
              v-if="isDiscountTypePercentage"
              v-model="serviceAssociation.discountAmount"
              trailing="%"
              :min="0"
              :max="100"
              slim
              class="w-1/2"
              :status="status"
              :disabled="serviceAssociation.isNewRecord && !target"
            />
            <segment-field v-model="serviceAssociation.discountType" :options="discountTypeOptions" class="border w-1/2" />
          </div>

          <a href="#" @click.stop.prevent="onRemoveDiscount" class="text-sm text-gray-500 hover:text-gray-700 mt-1">
            {{ $t('components.service.actions.editAddOn.removeDiscount') }}
          </a>
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { useServiceStore } from '~/stores/useServiceStore'
import ContentTemplateSelect from '@/components/contentTemplate/elements/Select.vue'
import ServiceSelect from '@/components/service/elements/Select.vue'
import {
  OAlert,
  OFieldGroup,
  OForm,
} from '@/components/elements'
import {
  TextField,
  SelectField,
  NumberField,
  SegmentField,
  ToggleField,
  CurrencyField,
  RadioField,
} from '@/components/fields'
import HelpGuide from '@/components/integration/edit/HelpGuide.vue'
import { ServiceAddOnMode } from '~/_api/models/service'
import {
  ServiceAssociation,
  ServiceAssociationOrderType,
  ServiceAssociationOrderTypeLabels,
  ServiceAssociationDiscountType,
  ServiceAssociationDiscountTypeLabels,
} from '~/_api/models/serviceAssociation'

const { $api, $notification, $i18n } = useNuxtApp()

const serviceStore = useServiceStore()

interface Props {
  service: Record<string, any>
  portal?: string
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
})

const emit = defineEmits<{
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'update:service', service: any): void
  (e: 'error'): void
}>()

const dialog = ref<any>()
const form = ref<any>()

const serviceAssociation = ref<any>(null)
const target = ref<any>(null)
const applyDiscount = ref(false)
const ServiceAssociationAddOnMode = ServiceAddOnMode

const services = computed(() => serviceStore.all)

const title = computed(() => {
  return serviceAssociation.value?.isNewRecord ? $i18n.t('components.service.actions.editAddOn.addTitle') : $i18n.t('components.service.actions.editAddOn.editTitle')
})

const options = computed(() => {
  return [
    { text: $i18n.t('general.cancel'), type: 'cancel' },
    { text: serviceAssociation.value?.isNewRecord ? $i18n.t('general.add') : $i18n.t('general.saveChanges'), type: 'confirm', callback: handleConfirm },
  ]
})

// ServiceAssociationOrderType and ServiceAssociationDiscountType are imported directly
// and available in template via script setup

const orderTypeOptions = computed(() => {
  return Object.values(ServiceAssociationOrderType)
    .filter((v): v is ServiceAssociationOrderType => typeof v === 'number')
    .map((value) => ({
      label: $i18n.t(ServiceAssociationOrderTypeLabels[value]),
      value,
    }))
})

const discountTypeOptions = computed(() => {
  return Object.values(ServiceAssociationDiscountType)
    .filter((v): v is ServiceAssociationDiscountType => typeof v === 'number')
    .map((value) => ({
      label: $i18n.t(ServiceAssociationDiscountTypeLabels[value]),
      value,
    }))
})

const isDiscountTypePercentage = computed(() => {
  return serviceAssociation.value.discountType === ServiceAssociationDiscountType.Percentage
})

const hasAddOnServices = computed(() => {
  return services.value.some((service: any) =>
    service.allowInternet &&
    (service.addOnMode === ServiceAddOnMode.allowed ||
    service.addOnMode === ServiceAddOnMode.only)
  )
})

watch(target, (service) => {
  if (!service || !serviceAssociation.value) return

  serviceAssociation.value.target_id = service.idService
  serviceAssociation.value.discountAmount = 0
})

watch(applyDiscount, (value) => {
  if (!value) {
    serviceAssociation.value.discountAmount = 0
  }
})

function open (serviceAddOn: any) {
  target.value = null
  if (serviceAddOn) {
    const data = (typeof structuredClone === 'function')
      ? structuredClone(serviceAddOn)
      : JSON.parse(JSON.stringify(serviceAddOn))

    serviceAssociation.value = new ServiceAssociation(data)
    applyDiscount.value = serviceAssociation.value.discountAmount > 0
  } else {
    setDefaults()
  }
  dialog.value.open()
}

function setDefaults () {
  const data = {
    target_type: 'service',
    type: 1,
    orderType: ServiceAssociationOrderType.After,
    discountType: ServiceAssociationDiscountType.Fixed,
  }

  const sa = new ServiceAssociation(data)
  serviceAssociation.value = sa
  applyDiscount.value = false
}

function validate () {
  return form.value.validate()
}

async function handleConfirm () {
  emit('update:loading', true)
  const isNewRecord = serviceAssociation.value.isNewRecord

  try {
    let service
    if (serviceAssociation.value.isNewRecord) {
      service = (await $api.service.addAssociation(props.service, serviceAssociation.value)).data
    } else {
      service = (await $api.service.updateAssociation(props.service, serviceAssociation.value)).data
    }

    if (service) {
      $notification.toast({
        title: $i18n.t(isNewRecord ? 'components.service.actions.editAddOn.addSuccess' : 'components.service.actions.editAddOn.editSuccess'),
        text: $i18n.t(isNewRecord ? 'components.service.actions.editAddOn.addSuccessInfo' : 'components.service.actions.editAddOn.editSuccessInfo', { title: props.service.title }),
        variant: 'success',
      })

      emit('success')
      emit('update:service', service)

      return true
    }
  } catch (e: any) {
    console.log(e)

    let errors = e?.errors

    if (!errors) {
      errors = $i18n.t(isNewRecord ? 'components.service.actions.editAddOn.addErrorInfo' : 'components.service.actions.editAddOn.editErrorInfo', { title: props.service.title })
    }

    $notification.error({
      title: $i18n.t(isNewRecord ? 'components.service.actions.editAddOn.addError' : 'components.service.actions.editAddOn.editError'),
      errors,
    })

    emit('error')
  }
}

function onAddDiscount () {
  applyDiscount.value = true
}

function onRemoveDiscount () {
  applyDiscount.value = false
  serviceAssociation.value.discountAmount = 0
}

function onClose () {
  dialog.value.close()
}

defineExpose({ open })
</script>
